import React, { useCallback, useEffect, useState, useRef } from "react";
import TablePagesTitle from "../../../components/table-pages-title/TablePagesTitle";
import AccessPageAction from "../page-action/AccessPageAction";
import { useNavigate, useParams } from "react-router-dom";
import { FaInfoCircle, FaCheck } from "react-icons/fa";
import { showToast } from "../../../utils/utils";
import {
  alertActions,
  buttonName,
  changeUserPass,
  fieldValidation,
  passwordtext,
  tooltips,
  userManage
} from "../../../locales/en";
import {
  DIGIT_REGEX,
  ESCAPE_REGEX,
  showToastType,
  UPPERCASE_REGEX
} from "../../../utils/constants/appConstants";
import {
  getChangePasswordURL,
  userManagementUsersURL
} from "../../../utils/constants/apiEndPoints";
import privateAxios from "../../../utils/axiosPrivate";
import { sanitizeInput } from "../../../utils/utils";
import { ADMIN_ACCESS_USER } from "../../../route/paths";
import "./../../../assets/scss/common.scss";

const ChangeUserPassword = () => {
  const initialRender = useRef(true);
  const [loading, setLoading] = useState(true);
  const history = useNavigate();
  const { passId } = useParams();
  const initialUserPassword = {
    userAccountId: "",
    password: "",
    confirmPassword: ""
  };
  const [userPassword, setUserPassword] = useState(initialUserPassword);
  const initialUserName = undefined;
  const [userName, setUsername] = useState(initialUserName);

  const initialErrors = {};
  const [errors, setErrors] = useState(initialErrors);

  const validatePassword = (password) => {
    const passwordErrors = [];

    if (password.length < 8) {
      passwordErrors.push(
        <small className="mt-1 d-block">
          <FaInfoCircle /> {changeUserPass.errmsg.item1}
        </small>
      );
    }
    if (!DIGIT_REGEX.test(password)) {
      passwordErrors.push(
        <small className="mt-1 d-block">
          <FaInfoCircle /> {changeUserPass.errmsg.item2}
        </small>
      );
    }
    if (!UPPERCASE_REGEX.test(password)) {
      passwordErrors.push(
        <small className="mt-1 d-block">
          <FaInfoCircle /> {changeUserPass.errmsg.item3}
        </small>
      );
    }
    if (!ESCAPE_REGEX.test(password)) {
      passwordErrors.push(
        <small className="mt-1 d-block">
          <FaInfoCircle /> {changeUserPass.errmsg.item4}
        </small>
      );
    }
    return passwordErrors;
  };

  const handleBlur = (e) => {
    const { name, value, type, checked } = e.target;

    const newUserPassword = {
      ...userPassword,
      [name]: type === "checkbox" ? checked : value
    };
    setUserPassword(newUserPassword);

    const tempErrors = { ...errors };

    if (name === "password") {
      const passwordErrors = validatePassword(value);
      tempErrors.password =
        passwordErrors.length > 0 ? (
          passwordErrors
        ) : (
          <small className="primary-color">
            <FaCheck /> {passwordtext?.okPassword}
          </small>
        );
    }

    if (name === "password" || name === "confirmPassword") {
      if (!newUserPassword.confirmPassword && name === "confirmPassword") {
        tempErrors.confirmPassword = fieldValidation.required;
      } else if (
        newUserPassword.confirmPassword &&
        newUserPassword.password !== newUserPassword.confirmPassword
      ) {
        tempErrors.confirmPassword = changeUserPass.errmsg.item5;
      } else if (
        newUserPassword.confirmPassword &&
        newUserPassword.password === newUserPassword.confirmPassword
      ) {
        tempErrors.confirmPassword = (
          <small className="primary-color">
            <FaCheck /> {changeUserPass.errmsg.item6}
          </small>
        );
      }
    }

    setErrors(tempErrors);
  };
  const onInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const cleanedValue = sanitizeInput(type === "checkbox" ? checked : value);

    const newUserPassword = {
      ...userPassword,
      [name]: cleanedValue
    };
    setUserPassword(newUserPassword);
  };

  const fecthUsername = useCallback(async () => {
    try {
      setLoading(true);
      const response = await privateAxios.get(userManagementUsersURL(passId));
      setUsername(response.data.data.firstName);
    } catch (error) {
      console.error(changeUserPass.errmsg.item7, error);
    } finally {
      setLoading(false);
    }
  }, [passId, privateAxios]);

  useEffect(() => {
    if (initialRender.current) {
      fecthUsername();
      initialRender.current = false;
    }
  }, [fecthUsername]);

  const validate = () => {
    const tempErrors = {};
    const showErrors = {};

    const passwordErrors = validatePassword(userPassword.password);
    if (passwordErrors.length > 0) {
      tempErrors.password = true;
      showErrors.password = passwordErrors;
    } else {
      showErrors.password = (
        <small className="primary-color">
          <FaCheck /> {passwordtext?.okPassword}
        </small>
      );
    }

    if (!userPassword.confirmPassword) {
      tempErrors.confirmPassword = true;
      showErrors.confirmPassword = fieldValidation.required;
    } else if (userPassword.password !== userPassword.confirmPassword) {
      tempErrors.confirmPassword = true;
      showErrors.confirmPassword = changeUserPass.errmsg.item5;
    } else {
      showErrors.confirmPassword = (
        <small className="primary-color">
          <FaCheck /> {changeUserPass.errmsg.item6}
        </small>
      );
    }

    setErrors(showErrors);

    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    try {
      const dataToSend = {
        ...userPassword,
        userAccountId: passId,
        password: sanitizeInput(userPassword.password),
        confirmPassword: sanitizeInput(userPassword.confirmPassword)
      };
      const response = await privateAxios.post(
        getChangePasswordURL,
        dataToSend
      );

      showToast(response.data.message, showToastType.success);

      history(ADMIN_ACCESS_USER);
    } catch (error) {
      if (error.response && error.response.status === 500) {
        showToast(error.response.data.message, showToastType.error);
      } else {
        showToast(error.message, showToastType.error);
      }
    }
  };

  const handleCancel = () => {
    history(ADMIN_ACCESS_USER);
  };

  if (loading) {
    return (
      <div className="spinner-wrap bg-light loader_absolute">
        <div className="spinner-border custom-spinner-color" />
      </div>
    );
  }

  return (
    <>
      <TablePagesTitle
        mainTitle={userManage.mainTitle}
        content={tooltips.changePassword}
      />
      <AccessPageAction
        subheading={`${changeUserPass.subH2} ${userName}`}
        alluser="/admin/access/user"
        allUserTitle={userManage.activeUser}
        createuser="/admin/access/user/create"
        createUserTitle={userManage.creteUser}
        deactivate="/admin/access/user/deactivated"
        deactivateTitle={userManage.deactiveUserBtn}
        deleteuser="/admin/access/user/deleted"
        deleteUserTitle={userManage.deleteUser}
      />
      <div className="row mb-3 mx-0">
        <div className="col-md-12 sale-leade-form chane_pass_frm mt-3">
          <form onSubmit={handleSubmit}>
            <div className="row mb-2">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="password">{changeUserPass.password}</label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="password"
                    name="password"
                    onChange={onInputChange}
                    onBlur={handleBlur}
                    value={userPassword.password}
                  />
                  {errors.password && (
                    <div className="text-danger small">{errors.password}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="passowrdConfirm">
                    {changeUserPass.passowrdConfirm}
                  </label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="password"
                    name="confirmPassword"
                    onChange={onInputChange}
                    onBlur={handleBlur}
                    value={userPassword.confirmPassword}
                  />
                  {errors.confirmPassword && (
                    <div className="text-danger small">
                      {errors.confirmPassword}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-sm-4" />
              <div className="col-sm-8 d-flex d-sm-block justify-content-center justify-content-sm-start">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn btn-red me-2"
                >
                  {alertActions.Cancel}
                </button>
                <button type="submit" className="btn btn-primary">
                  {buttonName.update}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ChangeUserPassword;
